import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { GrRevert } from "react-icons/gr";

import { StudentApi } from "api/studentsApi";
import {
  PaziaButton,
  PaziaInput,
  PaziaFormButton,
  PaziaMultiSelect,
  PaziaDateSelect,
} from "components/atoms";
import { PaziaCropDropzone } from "components/molecules";
import { ROLES_OPTIONS } from "consts/roles";
import { PaziaBox } from "components/atoms";
import { optionalEmail, optionalNip, optionalPesel, optionalPhone } from "utils/contactValidation";

export const STUDENT_FORM_FIELDS = {
  EMAIL: "email",
  FIRST_NAME: "firstName",
  PASSWORD: "password",
  CHANGE_PASSWORD: "changePassword",
  SURNAME: "surname",
  PHONE: "phone",
  PESEL: "pesel",
  ROLES: "roles",
  BORN_DATE: "bornDate",
  BORN_PLACE: "bornPlace",
  LICENCE_PHOTO: "licencePhoto",
  ADDRESS: "address",
  BUYER: "buyer",
  BUYER_ADDRESS: "buyerAddress",
  BUYER_NIP: "buyerNIP",
} as const;

interface UserFormProps {
  onSubmit: (data: UserFormValues) => Promise<void>;
  student?: StudentApi;
  disableEmail?: boolean;
}

export const UserForm = ({ onSubmit, student, disableEmail }: UserFormProps): JSX.Element | null => {
  const methods = useForm({
    resolver: yupResolver(schema),
    // Unchanged values saved earlier (e.g. by the shop, which has no phone format) are not re-checked on edit.
    context: { saved: student },
    defaultValues: {
      [STUDENT_FORM_FIELDS.EMAIL]: student?.email,
      [STUDENT_FORM_FIELDS.FIRST_NAME]: student?.firstName,
      [STUDENT_FORM_FIELDS.SURNAME]: student?.surname,
      [STUDENT_FORM_FIELDS.PHONE]: student?.phone,
      [STUDENT_FORM_FIELDS.PESEL]: student?.pesel,
      [STUDENT_FORM_FIELDS.CHANGE_PASSWORD]: false,
      [STUDENT_FORM_FIELDS.BORN_PLACE]: student?.bornPlace,
      [STUDENT_FORM_FIELDS.BORN_DATE]: student?.bornDate,
      [STUDENT_FORM_FIELDS.ROLES]: student?.roles?.map((role) => role.id.toString()),
      [STUDENT_FORM_FIELDS.LICENCE_PHOTO]: student?.licencePhoto ?? "",
      [STUDENT_FORM_FIELDS.ADDRESS]: student?.address,
      [STUDENT_FORM_FIELDS.BUYER]: student?.buyer,
      [STUDENT_FORM_FIELDS.BUYER_ADDRESS]: student?.buyerAddress,
      [STUDENT_FORM_FIELDS.BUYER_NIP]: student?.buyerNIP,
    },
  });
  const { handleSubmit, setValue, watch, formState } = methods;

  const editPassword = watch(STUDENT_FORM_FIELDS.CHANGE_PASSWORD);
  return (
    <PaziaBox>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="grid-cols-2 gap-8 md:grid">
          <div className="flex auto-rows-min grid-cols-2 flex-col gap-x-4 gap-y-2 md:grid md:gap-y-2">
            <PaziaInput disabled={disableEmail} required label="Email" name={STUDENT_FORM_FIELDS.EMAIL} />
            {student?.id &&
              (editPassword ? (
                <div className="flex items-end">
                  <PaziaInput type="password" label="Hasło" name={STUDENT_FORM_FIELDS.PASSWORD} />
                  <GrRevert
                    title="Nie zmieniaj hasła"
                    onClick={() => setValue(STUDENT_FORM_FIELDS.CHANGE_PASSWORD, false)}
                  />
                </div>
              ) : (
                <div className="flex items-end">
                  <PaziaButton
                    type="yellow2"
                    title="Zmień hasło"
                    btnType="button"
                    onClick={() => {
                      setValue(STUDENT_FORM_FIELDS.CHANGE_PASSWORD, true);
                    }}
                  />
                </div>
              ))}
            <PaziaInput required label="Imię" name={STUDENT_FORM_FIELDS.FIRST_NAME} className="col-start-1" />
            <PaziaInput required label="Nazwisko" name={STUDENT_FORM_FIELDS.SURNAME} />
            <PaziaInput label="Telefon" name={STUDENT_FORM_FIELDS.PHONE} />
            <PaziaInput label="PESEL" name={STUDENT_FORM_FIELDS.PESEL} />
            <PaziaInput label="Adres" name={STUDENT_FORM_FIELDS.ADDRESS} className="col-span-2" />
            <PaziaInput label="Nabywca" name={STUDENT_FORM_FIELDS.BUYER} />
            <PaziaInput label="NIP nabywcy" name={STUDENT_FORM_FIELDS.BUYER_NIP} />
            <PaziaInput label="Adres nabywcy" name={STUDENT_FORM_FIELDS.BUYER_ADDRESS} className="col-span-2" />
            <PaziaMultiSelect
              label="Rola w systemie"
              required
              name={STUDENT_FORM_FIELDS.ROLES}
              options={ROLES_OPTIONS}
              className="col-span-2"
            />
          </div>
          <div className="flex auto-rows-min grid-cols-2 flex-col gap-1 gap-x-8 gap-y-2 md:grid md:gap-y-4">
            <PaziaDateSelect
              label="Data urodzenia"
              name={STUDENT_FORM_FIELDS.BORN_DATE}
              range={[new Date(1900, 0, 1), new Date()]}
            />
            <PaziaInput label="Miejsce urodzenia" name={STUDENT_FORM_FIELDS.BORN_PLACE} />
            <div className="relative col-span-2 h-full">
              <PaziaCropDropzone name={STUDENT_FORM_FIELDS.LICENCE_PHOTO} label="Zdjęcie do dokumentów" />
            </div>
          </div>
          <PaziaFormButton title="Zapisz" className="col-start-2 mt-4 md:mt-0" />
          {/* TODO Dirty disabled*/}
        </form>
      </FormProvider>
    </PaziaBox>
  );
};

const schema = yup
  .object({
    // The email is the login: "nie-email" used to be accepted on create.
    [STUDENT_FORM_FIELDS.EMAIL]: optionalEmail().required(),
    [STUDENT_FORM_FIELDS.FIRST_NAME]: yup.string().required(),
    [STUDENT_FORM_FIELDS.SURNAME]: yup.string().required(),
    [STUDENT_FORM_FIELDS.PHONE]: optionalPhone(),
    [STUDENT_FORM_FIELDS.CHANGE_PASSWORD]: yup.boolean(),
    [STUDENT_FORM_FIELDS.PASSWORD]: yup
      .string()
      .nullable()
      .when(STUDENT_FORM_FIELDS.CHANGE_PASSWORD, {
        is: true,
        then: (shema) =>
          shema
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/,
              "Hasło musi zawierać przynajmniej jedną wielką literę, jedną małą, jedną cyfrę i jeden znak specjalny.",
            )
            .required(),
      }),
    [STUDENT_FORM_FIELDS.PESEL]: optionalPesel(),
    [STUDENT_FORM_FIELDS.ROLES]: yup.array().of(yup.string()).min(1, "Wybierz przynajmniej jedną").required(),
    [STUDENT_FORM_FIELDS.BORN_DATE]: yup.date().nullable(),
    [STUDENT_FORM_FIELDS.BORN_PLACE]: yup.string().nullable(),
    [STUDENT_FORM_FIELDS.LICENCE_PHOTO]: yup.mixed(),
    [STUDENT_FORM_FIELDS.ADDRESS]: yup.string().nullable(),
    [STUDENT_FORM_FIELDS.BUYER]: yup.string().nullable(),
    [STUDENT_FORM_FIELDS.BUYER_ADDRESS]: yup.string().nullable(),
    [STUDENT_FORM_FIELDS.BUYER_NIP]: optionalNip(),
  })
  .required();
export type UserFormValues = yup.InferType<typeof schema>;
